// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo, useState } from 'react';
import { StyleSheet, type LayoutChangeEvent } from 'react-native';
import Svg, { Line, Polyline, Text as SvgText } from 'react-native-svg';

import { Text, View } from '@/components/Themed';
import appColors from '@/theme/colors';
import { seriesColor } from '@/theme/seriesColors';

export type PositionByHoleSeriesEntry = { playerId: string; totals: number[] };

type Props = {
  holes: number[];
  series: PositionByHoleSeriesEntry[];
  playerNameById: Map<string, string>;
};

export function PositionByHoleGraph({ holes, series, playerNameById }: Props) {
  const [containerWidth, setContainerWidth] = useState(0);
  const width = useMemo(() => {
    if (containerWidth <= 0) {
      return 320;
    }
    return Math.max(280, containerWidth);
  }, [containerWidth]);

  // Leaderboard position at each hole from the cumulative merit totals: standard
  // competition ranking, so tied players share the same position.
  const positionSeries = useMemo(() => {
    const count = holes.length;
    return series.map((entry) => ({
      playerId: entry.playerId,
      positions: Array.from({ length: count }, (_, holeIndex) => {
        const own = entry.totals[holeIndex] ?? 0;
        const ahead = series.filter((other) => (other.totals[holeIndex] ?? 0) > own).length;
        return ahead + 1;
      }),
    }));
  }, [holes.length, series]);

  const playerCount = Math.max(1, series.length);
  const height = 60 + playerCount * 34;
  const paddingLeft = 34;
  const paddingRight = 12;
  const paddingTop = 12;
  const paddingBottom = 26;
  const plotWidth = width - paddingLeft - paddingRight;
  const plotHeight = height - paddingTop - paddingBottom;

  const count = holes.length;
  const xFor = (index: number) => paddingLeft + (count > 1 ? (index * plotWidth) / (count - 1) : plotWidth / 2);
  const yFor = (position: number) =>
    paddingTop + (playerCount > 1 ? ((position - 1) * plotHeight) / (playerCount - 1) : plotHeight / 2);

  const positionLabels = Array.from({ length: playerCount }, (_, index) => index + 1);
  const holeLabelStep = count > 9 ? 3 : 1;

  const handleLayout = (event: LayoutChangeEvent) => {
    const nextWidth = event.nativeEvent.layout.width;
    if (nextWidth > 0 && Math.abs(nextWidth - containerWidth) > 1) {
      setContainerWidth(nextWidth);
    }
  };

  return (
    <View style={styles.wrap} onLayout={handleLayout}>
      <Svg width={width} height={height}>
        {positionLabels.map((position) => (
          <Line
            key={`grid-${position}`}
            x1={paddingLeft}
            y1={yFor(position)}
            x2={width - paddingRight}
            y2={yFor(position)}
            stroke="rgba(255,255,255,0.14)"
            strokeWidth={1}
          />
        ))}
        {positionLabels.map((position) => (
          <SvgText
            key={`y-${position}`}
            x={paddingLeft - 6}
            y={yFor(position) + 4}
            fontSize="10"
            fill="#C8C8B0"
            textAnchor="end"
          >
            {position}
          </SvgText>
        ))}
        {holes.map((hole, index) => {
          if (hole !== 1 && hole % holeLabelStep !== 0) {
            return null;
          }
          return (
            <SvgText
              key={`x-${hole}`}
              x={xFor(index)}
              y={height - paddingBottom + 16}
              fontSize="10"
              fill="#C8C8B0"
              textAnchor="middle"
            >
              {hole}
            </SvgText>
          );
        })}
        {positionSeries.map((entry, index) => {
          // Tied players share a position; nudge each line a little so overlapping
          // strands stay individually visible.
          const tieOffset = (index - (positionSeries.length - 1) / 2) * 2;
          const points = entry.positions
            .map((position, idx) => `${xFor(idx)},${yFor(position) + tieOffset}`)
            .join(' ');
          return (
            <Polyline
              key={entry.playerId}
              points={points}
              fill="none"
              stroke={seriesColor(index)}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          );
        })}
      </Svg>
      <View style={styles.legend}>
        {positionSeries.map((entry, index) => {
          const finalPosition = entry.positions.length > 0 ? entry.positions[entry.positions.length - 1] : 0;
          return (
            <View key={entry.playerId} style={styles.legendItem}>
              <View style={[styles.legendSwatch, { backgroundColor: seriesColor(index) }]} />
              <Text style={styles.legendLabel}>
                {playerNameById.get(entry.playerId) ?? entry.playerId} · #{finalPosition}
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    backgroundColor: 'transparent',
    gap: 6,
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    backgroundColor: 'transparent',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'transparent',
  },
  legendSwatch: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  legendLabel: {
    color: appColors.text.primary,
    fontSize: 12,
    fontFamily: 'Sanchez',
  },
});
